import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Search, Package } from 'lucide-react';
import { getMineralSymbol, getMineralColor } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function MineralsPage() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [minerals, setMinerals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    commodity: '',
    country: '',
    category: '',
    search: ''
  });

  useEffect(() => {
    fetchMinerals();
  }, [filters]);

  const fetchMinerals = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.commodity) params.append('commodity', filters.commodity);
      if (filters.country) params.append('origin_country', filters.country);
      
      const response = await axios.get(`${API}/minerals?${params}`, {
        headers: getAuthHeaders()
      });
      
      let filteredMinerals = response.data;
      if (filters.search) {
        filteredMinerals = filteredMinerals.filter(mineral => 
          mineral.commodity.toLowerCase().includes(filters.search.toLowerCase()) ||
          mineral.description.toLowerCase().includes(filters.search.toLowerCase())
        );
      }
      if (filters.category) {
        filteredMinerals = filteredMinerals.filter(m => m.commodity_category === filters.category);
      }
      
      setMinerals(filteredMinerals);
    } catch (error) {
      console.error('Failed to fetch minerals:', error);
    } finally {
      setLoading(false);
    }
  };

  const COMMODITY_CATEGORIES = ['Base Metals', 'Battery Minerals', 'Bulk Minerals', 'Energy Commodities', 'Industrial Minerals', 'Precious Metals', 'Specialty Materials'];
  const commodities = ['Bauxite', 'Chromite', 'Coal', 'Cobalt', 'Copper', 'Diamonds', 'Gold', 'Graphite', 'Iron Ore', 'Lithium', 'Manganese', 'Nickel', 'Platinum', 'Silver', 'Tantalum', 'Tin', 'Uranium', 'Zinc'].sort();
  const countries = ['Algeria', 'Angola', 'Bahrain', 'Benin', 'Botswana', 'Burkina Faso', 'Burundi', 'Cameroon', 'Cape Verde', 'Central African Republic', 'Chad', 'Comoros', 'Cote d\'Ivoire', 'Djibouti', 'DRC', 'Egypt', 'Equatorial Guinea', 'Eritrea', 'Eswatini', 'Ethiopia', 'Gabon', 'Gambia', 'Ghana', 'Guinea', 'Guinea-Bissau', 'Iraq', 'Iran', 'Israel', 'Jordan', 'Kenya', 'Kuwait', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Madagascar', 'Malawi', 'Mali', 'Mauritania', 'Mauritius', 'Morocco', 'Mozambique', 'Namibia', 'Niger', 'Nigeria', 'Oman', 'Palestine', 'Qatar', 'Republic of Congo', 'Rwanda', 'Saudi Arabia', 'Senegal', 'Seychelles', 'Sierra Leone', 'Somalia', 'South Africa', 'South Sudan', 'Sudan', 'Syria', 'Tanzania', 'Togo', 'Tunisia', 'Turkey', 'UAE', 'Uganda', 'Yemen', 'Zambia', 'Zimbabwe'];

  return (
    <div className="p-6 space-y-6" data-testid="minerals-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Mineral Brokerage</h1>
          <p className="text-muted-foreground">Discover commodity offtake opportunities</p>
        </div>
        <Button onClick={() => navigate('/deals/submit', { state: { dealType: 'minerals' } })} data-testid="submit-mineral-button">
          Submit Mineral Listing
        </Button>
      </div>

      <Card data-testid="mineral-filters">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={16} />
              <Input
                placeholder="Search minerals..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10"
                data-testid="search-input"
              />
            </div>

            <Select value={filters.category} onValueChange={(value) => setFilters({ ...filters, category: value })}>
              <SelectTrigger data-testid="category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {COMMODITY_CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filters.commodity} onValueChange={(value) => setFilters({ ...filters, commodity: value })}>
              <SelectTrigger data-testid="commodity-filter">
                <SelectValue placeholder="All Commodities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Commodities</SelectItem>
                {commodities.map(commodity => (
                  <SelectItem key={commodity} value={commodity}>{commodity}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.country} onValueChange={(value) => setFilters({ ...filters, country: value })}>
              <SelectTrigger data-testid="country-filter">
                <SelectValue placeholder="All Countries" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Countries</SelectItem>
                {countries.map(country => (
                  <SelectItem key={country} value={country}>{country}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Loading minerals...</div>
        </div>
      ) : minerals.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              No minerals found matching your criteria
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {minerals.map((mineral) => (
            <Card 
              key={mineral.id} 
              className="hover:shadow-md transition-shadow cursor-pointer" 
              onClick={() => navigate(`/minerals/${mineral.id}`)}
              data-testid={`mineral-card-${mineral.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-12 h-12 rounded flex items-center justify-center font-mono font-bold text-white text-lg"
                      style={{ backgroundColor: getMineralColor(mineral.commodity) }}
                    >
                      {getMineralSymbol(mineral.commodity)}
                    </div>
                    <div>
                      <CardTitle className="text-base">{mineral.commodity}</CardTitle>
                      <p className="text-xs text-muted-foreground mt-1">{mineral.origin_country}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge className={mineral.status === 'available' ? 'bg-green-100 text-green-700' : mineral.status === 'pending_approval' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'}>
                      {mineral.status?.replace(/_/g, ' ')}
                    </Badge>
                    {mineral.verification_status === 'verified' && <Badge className="bg-green-100 text-green-700 text-[9px]">Verified</Badge>}
                    {mineral.commodity_category && <Badge variant="outline" className="text-[9px]">{mineral.commodity_category}</Badge>}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3 py-2 border-t border-b border-border">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Grade</p>
                    <p className="text-xs font-mono font-semibold mt-1">{mineral.grade}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Volume</p>
                    <p className="text-xs font-mono font-semibold mt-1">{mineral.volume.toLocaleString()} {mineral.volume_unit}</p>
                  </div>
                </div>
                
                <p className="text-xs text-muted-foreground line-clamp-2">{mineral.description}</p>
                
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Delivery:</span>
                    <span className="font-medium">{mineral.delivery_basis}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Contract:</span>
                    <span className="font-medium">{mineral.contract_duration}</span>
                  </div>
                  {mineral.financing_need && (
                    <Badge className="mt-2 text-xs">Financing Required</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
